/**
 * Phase 8.3.1 — Kredit-System.
 *
 * Time semantics:
 *   - Game-time milliseconds are the canonical clock.
 *   - 1 game-day = 86_400_000 ms.
 *   - 1 game-week = 7 game-days.
 * Both daily upkeep deduction and weekly loan interest accrue on these
 * boundaries (independent of the player's speed multiplier — interest fires
 * after enough simulated time, regardless of wall-clock).
 */

import type { Loan } from '@/state/types';

export const GAME_DAY_MS = 86_400_000;
export const GAME_WEEK_MS = 7 * GAME_DAY_MS;

/** 5 % per game-week, applied to the remaining principal. */
export const LOAN_INTEREST_RATE_WEEKLY = 0.05;

/** Per-loan amount range. */
export const LOAN_MIN_AMOUNT = 100_000;
export const LOAN_MAX_AMOUNT = 10_000_000;

/**
 * Cap on TOTAL outstanding loan volume.
 *
 * The user spec says "cap at 5×dailyUpkeep". A literal reading would make the
 * cap impossibly small (early game upkeep is a few thousand euros). We
 * interpret it as the player can borrow up to five MONTHS of their daily
 * upkeep — enough to bridge a long unprofitable stretch but not enough to
 * trivialise expansion. The hard floor is 1 M € so brand-new accounts can
 * still pull a starter loan even before their dailyUpkeep climbs.
 */
export function maxOutstandingLoan(dailyUpkeep: number): number {
  return Math.max(1_000_000, dailyUpkeep * 30 * 5);
}

/** Sum of remainingPrincipal across all loans. */
export function totalOutstanding(loans: Loan[]): number {
  let s = 0;
  for (const l of loans) s += l.remainingPrincipal;
  return s;
}

/**
 * How much could the player still borrow right now?
 * `min(LOAN_MAX_AMOUNT, maxOutstanding - currentOutstanding)`, but never < 0.
 */
export function availableLoanCapacity(loans: Loan[], dailyUpkeep: number): number {
  const cap = maxOutstandingLoan(dailyUpkeep);
  const out = totalOutstanding(loans);
  return Math.max(0, Math.min(LOAN_MAX_AMOUNT, cap - out));
}

/**
 * Returns the new loan after accruing one week of interest.
 * Interest is added to both `remainingPrincipal` and `accruedInterest`.
 */
export function applyWeeklyInterest(loan: Loan): {
  loan: Loan;
  interestAdded: number;
} {
  const interest = Math.round(loan.remainingPrincipal * loan.interestRatePerWeek);
  return {
    loan: {
      ...loan,
      remainingPrincipal: loan.remainingPrincipal + interest,
      accruedInterest: loan.accruedInterest + interest,
      lastInterestAt: loan.lastInterestAt + GAME_WEEK_MS,
    },
    interestAdded: interest,
  };
}
